import {
  faArrowUpRightFromSquare,
  faLocationDot,
  faStar,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, memo } from 'react';
import { photoUrl } from '@/api/places';
import { AddToTripButton } from '@/design-system/composite/AddToTripButton';
import type { ItineraryItem } from '@/types/itinerary';
import { formatAddress } from '@/utils/address';
import { categoryDefinitions, humanizeCategoryLabel } from '@/utils/categories';
import { getTripDayHours } from '@/utils/schedule';
import { tw, twx } from '@/utils/tailwind';
import { formatDuration } from '@/utils/visitDuration';

export interface ItineraryCardProps {
  item: ItineraryItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  /** Active trip's date range — used to compute per-day hours.
   * A stable two-string object so React.memo's shallow compare works; this
   * card no longer subscribes to the trip context directly, which kept every
   * card re-rendering on any trip mutation (e.g., adding a place). */
  tripDates: { startDate: string; endDate: string } | null;
}

const formatPrice = (price?: number): string | null => (price ? '$'.repeat(price) : null);

const formatDistance = (miles?: number): string | null => {
  if (miles === undefined) return null;
  return miles < 0.1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;
};

const formatRating = (rating?: number): string | null =>
  rating !== undefined ? rating.toFixed(1) : null;

const trailingZeroDecimal = /\.0$/;

const formatRatingCount = (count?: number): string | null => {
  if (count === undefined) return null;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(trailingZeroDecimal, '')}k`;
  return String(count);
};

const DayLabel: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const formatTime = (t: string): string => {
  const h = Number(t.slice(0, 2));
  const m = t.slice(2);
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === '00' ? `${h12}${suffix}` : `${h12}:${m}${suffix}`;
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

const iconStyle = (size: number, vars?: Record<`--fa-${string}`, string>): FAIconStyle =>
  ({ width: size, height: size, ...vars }) as FAIconStyle;

const ItineraryCardImpl = ({ item, isSelected, onSelect, tripDates }: ItineraryCardProps) => {
  const def = categoryDefinitions[item.appCategory];
  const isUserPin = item.source === 'user';
  const photo = item.photos[0];
  const price = formatPrice(item.price);
  const distance = formatDistance(item.distanceMiles);
  const rating = formatRating(item.rating);
  const ratingCount = formatRatingCount(item.userRatingCount);
  const duration = formatDuration(item.estimatedDuration);
  const isClosed =
    item.businessStatus === 'PERMANENTLY_CLOSED' || item.businessStatus === 'TEMPORARILY_CLOSED';

  const today = new Date().getDay() || 7; // 1=Mon…7=Sun
  const todayHours = item.hours?.regular?.find((h) => h.day === today);

  const tripDayHours =
    tripDates && item.hours?.regular && item.hours.regular.length > 0
      ? getTripDayHours(tripDates, item.hours.regular)
      : null;

  return (
    <article
      className={twx(
        'relative w-full bg-white rounded-2xl overflow-hidden transition-all duration-150 group',
        'border motion-safe:will-change-transform',
        isSelected
          ? 'border-secondary-emphasized shadow-md ring-2 ring-secondary-emphasized/20'
          : // Hover-lift: small translate + stronger shadow on hover. Honors
            // prefers-reduced-motion via motion-safe:.
            'border-content-border hover:border-secondary-main/50 hover:shadow-lg motion-safe:hover:-translate-y-0.5',
      )}
    >
      {/* Stretched select button — covers the card; interactive children use relative z-10 to sit above it */}
      <button
        type="button"
        onClick={() => onSelect(item.id)}
        aria-label={`View details for ${item.name}`}
        className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary-emphasized/50"
      />

      {/* Photo — 16:9 aspect (was 5:2, ~40% taller). When the place has no
          photo we skip the entire block: per user preference, no fallback
          art. Cards become shorter and the meta block carries the visual
          weight. The category badge always shows in the content area
          below, so identity isn't lost when there's no photo. */}
      {photo && (
        // pointer-events-none so clicks pass through to the absolute
        // select button beneath (the photo div would otherwise eat the
        // click since it paints above the button in CSS stacking order
        // — same parent, both positioned, photo later in tree). Hover
        // animation (group-hover:scale-105) still fires because that
        // uses the `group` class on the article, not pointer events.
        <div className="relative w-full h-28 md:h-auto md:aspect-[16/9] overflow-hidden bg-content-focal pointer-events-none">
          <div
            aria-hidden
            className="absolute inset-0 bg-kawaii-shimmer motion-safe:animate-kawaii-shimmer"
          />
          <img
            src={photoUrl(photo, 400)}
            alt={item.name}
            className="relative w-full h-full object-cover transition-[transform,opacity] duration-300 group-hover:scale-105 opacity-0"
            loading="lazy"
            onLoad={(e) => {
              (e.target as HTMLImageElement).classList.remove('opacity-0');
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement?.classList.add('hidden');
            }}
          />
          {isClosed && (
            <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-error-subtle text-error-main border-error-main/20 shadow-sm">
              {item.businessStatus === 'PERMANENTLY_CLOSED' ? 'Closed perm.' : 'Closed temp.'}
            </span>
          )}
        </div>
      )}

      <div className="p-3 space-y-1.5">
        {/* Title row — title first, category chip to the right. Flex-wrap
            lets the chip drop to a second line when the title needs the
            whole row (long names). Title `basis-[60%]` is the threshold
            that triggers the wrap. */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="grow basis-[60%] min-w-0 font-heading font-semibold text-base text-content-text leading-tight line-clamp-2">
            {item.name}
          </h3>
          <span
            className={tw`
              shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border leading-none
              ${def.badgeClasses.bg} ${def.badgeClasses.text} ${def.badgeClasses.border}
            `}
            title={isUserPin ? 'A custom pin you added' : undefined}
          >
            <FontAwesomeIcon icon={def.icon} style={{ width: 10, height: 10 }} />
            {isUserPin
              ? `Custom · ${def.label}`
              : item.categories[0]?.name
                ? humanizeCategoryLabel(item.categories[0].name)
                : def.label}
          </span>
          {/* Closed badge here only when there's no photo (the photo
              variant overlays it on the image). Keeps the "this place
              is closed" signal prominent regardless of layout. */}
          {isClosed && !photo && (
            <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-error-subtle text-error-main border-error-main/20">
              {item.businessStatus === 'PERMANENTLY_CLOSED' ? 'Closed perm.' : 'Closed temp.'}
            </span>
          )}
        </div>

        {/* Editorial summary — one line only in the compact card. */}
        {item.editorialSummary && (
          <p className="text-xs text-content-subtle leading-relaxed line-clamp-1">
            {item.editorialSummary}
          </p>
        )}

        {/* Compact meta line. All meta facts join here, separated by
            mid-dots. Each segment is rendered conditionally so a missing
            field just shortens the line (no awkward empty dot pairs).
            Wraps to a second line at narrow widths. */}
        <CompactMeta
          rating={rating}
          ratingCount={ratingCount}
          price={price}
          distance={distance}
          openLabel={
            item.hours ? (item.hours.isOpenNow ? 'Open now' : 'Closed') : 'Hours unavailable'
          }
          openTone={item.hours ? (item.hours.isOpenNow ? 'success' : 'error') : 'muted'}
          todayHoursLabel={
            todayHours && !tripDayHours
              ? `${DayLabel[todayHours.day]} ${formatTime(todayHours.open)}–${formatTime(todayHours.close)}`
              : null
          }
          duration={duration === 'Variable' ? null : duration}
          address={formatAddress(item.address)}
        />

        {/* Trip-day hours inline row. Drops the "DURING YOUR TRIP"
            eyebrow + multi-line block in favor of a single wrap-able
            line of day chips. */}
        {tripDayHours && (
          <div className="flex flex-wrap gap-x-2 gap-y-0 text-[11px] leading-snug">
            {tripDayHours.map(({ isoDate, hours }) => {
              const dayAbbr = new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
                weekday: 'short',
              });
              return (
                // inline-flex + items-center vertically aligns the
                // closed-day xmark icon with the day-name baseline.
                // The unicode "×" character was rendering small + off-
                // baseline at this small font size; FontAwesome xmark
                // sits cleanly and matches the icon language used
                // elsewhere in the card.
                <span key={isoDate} className="inline-flex items-center gap-1 whitespace-nowrap">
                  <span className="text-content-disabled">{dayAbbr}</span>
                  {hours ? (
                    <span className="text-content-text">
                      {formatTime(hours.open)}–{formatTime(hours.close)}
                    </span>
                  ) : (
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="text-error-main"
                      style={{ width: 10, height: 10 }}
                      aria-label="Closed"
                    />
                  )}
                </span>
              );
            })}
          </div>
        )}

        {/* Action row */}
        <div className="relative z-10 flex gap-3 pt-0.5 flex-wrap items-center">
          {item.website && (
            <a
              href={item.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-xs text-secondary-emphasized hover:underline font-medium"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={iconStyle(11)} />
              Website
            </a>
          )}
          <a
            href={`https://maps.apple.com/?q=${encodeURIComponent(item.name)}&ll=${item.latitude},${item.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-xs text-secondary-emphasized hover:underline font-medium"
          >
            <FontAwesomeIcon icon={faLocationDot} style={iconStyle(11)} />
            Maps
          </a>
          <AddToTripButton item={item} variant="inline" />
        </div>
      </div>
    </article>
  );
};

interface CompactMetaProps {
  rating: string | null;
  ratingCount: string | null;
  price: string | null;
  distance: string | null;
  openLabel: string;
  openTone: 'success' | 'error' | 'muted';
  todayHoursLabel: string | null;
  duration: string | null;
  address: string;
}

/** Joins a variable set of metadata facts into a single wrap-able row
 * with mid-dot separators. Each fact is conditionally pushed so the
 * separator math stays correct when fields are missing. */
const CompactMeta = ({
  rating,
  ratingCount,
  price,
  distance,
  openLabel,
  openTone,
  todayHoursLabel,
  duration,
  address,
}: CompactMetaProps) => {
  const openClass =
    openTone === 'success'
      ? 'text-success-main font-medium'
      : openTone === 'error'
        ? 'text-error-main font-medium'
        : 'text-content-subtle';

  const segments: Array<{ key: string; node: React.ReactNode }> = [];
  if (rating)
    segments.push({
      key: 'rating',
      node: (
        <span className="flex items-center gap-1">
          <FontAwesomeIcon
            icon={faStar}
            style={iconStyle(11, {
              '--fa-primary-color': 'var(--color-tertiary-main)',
              '--fa-secondary-color': 'var(--color-tertiary-main)',
            })}
          />
          <span className="tabular-nums text-content-text">{rating}</span>
          {ratingCount && <span className="text-content-disabled">({ratingCount})</span>}
        </span>
      ),
    });
  if (price)
    segments.push({
      key: 'price',
      node: <span className="font-mono text-content-deemphasized">{price}</span>,
    });
  if (distance) segments.push({ key: 'distance', node: <span>{distance}</span> });
  segments.push({ key: 'open', node: <span className={openClass}>{openLabel}</span> });
  if (todayHoursLabel) segments.push({ key: 'today', node: <span>{todayHoursLabel}</span> });
  if (duration) segments.push({ key: 'duration', node: <span>{duration}</span> });
  if (address)
    segments.push({
      key: 'addr',
      node: <span className="truncate min-w-0">{address}</span>,
    });

  return (
    <div className="text-xs text-content-subtle flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
      {segments.map((seg, i) => (
        <Fragment key={seg.key}>
          {i > 0 && <span className="text-content-disabled leading-none">·</span>}
          {seg.node}
        </Fragment>
      ))}
    </div>
  );
};

// Default shallow-prop comparison is sufficient: `item`, `onSelect`, and
// `tripDates` are stable references from the parent (`ItineraryList`), and
// `isSelected` is a primitive. The card no longer subscribes to trip context
// directly, so trip mutations don't cascade through here.
export const ItineraryCard = memo(ItineraryCardImpl);
